import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { BoussoleTab } from "../components/boussole/BoussoleTab";
import { InspirationTab } from "../components/inspiration/InspirationTab";

const LEVEL = { p0: "border-red-500/50 bg-red-500/10 text-red-100", p1: "border-amber-500/40 bg-amber-500/10 text-amber-100", p2: "border-sky-500/30 bg-sky-500/10 text-sky-100", ok: "border-emerald-500/30 bg-emerald-500/5 text-emerald-100" };
const STATUS_LABEL = { brouillon: "Brouillon", a_verifier: "À vérifier", approuve: "Approuvé", publie: "Publié", a_reviser: "À réviser", archive: "Archivé", non_suivi: "Non suivi" };
const card = "rounded-2xl border border-white/10 bg-white/[0.03] p-4";

export default function StandardQualiteModule() {
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "tableau");
  const [dash, setDash] = useState(null);
  const load = useCallback(async () => { try { const r = await api.get("/quality/dashboard"); setDash(r.data); } catch { toast.error("Erreur tableau"); } }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="standard-qualite">
      <div>
        <div className="text-[10px] tracking-[0.35em] text-[#C9A961]">STANDARD INSTITUT IMPACT</div>
        <h1 className="text-2xl sm:text-3xl font-bold">Qualité & confiance</h1>
        <p className="text-sm text-zinc-400">Clair · Exact · Sécuritaire · Suivi · Digne de notre nom. Rien ne passe en « Publié » sans checklist 5/5.</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {[["tableau", "📊 Tableau"], ["boussole", "🧭 Boussole Impact"], ["inspiration", "✨ Inspiration"], ["gouvernance", "🗂️ Gouvernance & checklist"], ["rapprochements", "🔗 Rapprochements"], ["historique", "🕓 Historique"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`sq-tab-${k}`} className={`rounded-full px-4 py-1.5 text-sm border ${tab === k ? "border-[#C9A961] bg-[#C9A961]/15 text-white" : "border-white/10 text-zinc-400 hover:bg-white/5"}`}>{l}</button>
        ))}
      </div>
      {tab === "tableau" && <Tableau dash={dash} />}
      {tab === "boussole" && <BoussoleTab />}
      {tab === "inspiration" && <InspirationTab />}
      {tab === "gouvernance" && <Gouvernance pages={dash?.pages || []} onChange={load} />}
      {tab === "rapprochements" && <Rapprochements onChange={load} />}
      {tab === "historique" && <Historique rows={dash?.history || []} />}
    </div>
  );
}

function Tableau({ dash }) {
  if (!dash) return <p className="text-sm text-zinc-500">Chargement…</p>;
  return (
    <div className="space-y-4">
      <div className={`${card} flex flex-wrap items-center gap-6`} data-testid="sq-score">
        <div><div className="text-xs uppercase tracking-widest text-zinc-400">Score confiance</div><div className={`text-5xl font-black ${dash.score >= 80 ? "text-emerald-300" : dash.score >= 50 ? "text-amber-300" : "text-red-300"}`}>{dash.score} %</div></div>
        <div className="text-xs text-zinc-400 max-w-md">{dash.items.filter((i) => i.level === "p0").length} P0 · {dash.items.filter((i) => i.level === "p1").length} P1 · {dash.items.filter((i) => i.level === "p2").length} P2 — généré {new Date(dash.generated_at).toLocaleString("fr-CA")}</div>
        <div className="ml-auto text-xs" data-testid="sq-automations">
          <div className="uppercase tracking-widest text-zinc-400 mb-1">Automatisations</div>
          {dash.automations.map((a) => <div key={a.id} className="flex items-center gap-2"><span className={`w-2 h-2 rounded-full ${a.on ? "bg-emerald-400" : "bg-zinc-600"}`} />{a.label} <span className="text-zinc-500">{a.on ? "ON" : "OFF"}</span></div>)}
        </div>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sq-items">
        {dash.items.map((i) => (
          <a key={i.id} href={i.link} className={`rounded-xl border p-4 block hover:brightness-110 ${LEVEL[i.level]}`} data-testid={`sq-item-${i.id}`}>
            <div className="flex items-start justify-between gap-2"><span className="text-sm">{i.label}</span><span className="text-[10px] uppercase font-bold">{i.level}</span></div>
            <div className="text-3xl font-black mt-2">{i.value}</div>
          </a>
        ))}
      </div>
    </div>
  );
}

function Gouvernance({ pages, onChange }) {
  const [items, setItems] = useState([]);
  const [sel, setSel] = useState(null);
  const [answers, setAnswers] = useState([false, false, false, false, false]);
  const [status, setStatus] = useState("a_verifier");
  const [owner, setOwner] = useState("");
  const QUESTIONS = ["Est-ce clair ?", "Est-ce exact ?", "Est-ce sécuritaire ?", "Est-ce suivi ?", "Est-ce digne de notre nom ?"];
  const load = useCallback(async () => { const r = await api.get("/quality/governance"); setItems(r.data.items); }, []);
  useEffect(() => { load(); }, [load]);
  const targets = [...pages.map((p) => ({ target_type: "page", target_id: p.path, label: p.path })),
    ...["plan:adhesion", "prix:petits-samourais", "prix:jeux-martiaux", "boutique:dropship", "courriel:rentree-5-actions", "automatisation:samourais_comms", "formulaire:petits-samourais", "formulaire:jm-v2"].map((t) => ({ target_type: t.split(":")[0], target_id: t.split(":")[1], label: t }))];
  const current = (t) => items.find((i) => i.target_type === t.target_type && i.target_id === t.target_id);
  const submit = async () => {
    try {
      const c = await api.post("/quality/checklist", { ...sel, answers });
      toast[c.data.check.passed ? "success" : "warning"](c.data.verdict);
      const r = await api.post("/quality/governance", { ...sel, label: sel.label, status, owner });
      toast.success(`Statut : ${STATUS_LABEL[r.data.item.status]}`); setSel(null); load(); onChange();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className={card} data-testid="sq-gov-list">
        <div className="text-xs uppercase tracking-widest text-zinc-400 mb-3">Éléments suivis</div>
        <div className="space-y-1.5">
          {targets.map((t) => { const g = current(t); return (
            <button key={t.label} onClick={() => { setSel(t); setStatus(g?.status || "a_verifier"); setOwner(g?.owner || ""); setAnswers([false, false, false, false, false]); }} data-testid={`sq-gov-${t.label.replace(/[^a-z0-9]/gi, "-")}`}
              className={`w-full text-left rounded-lg border px-3 py-2 text-sm flex items-center gap-2 ${sel?.label === t.label ? "border-[#C9A961]" : "border-white/10 hover:bg-white/5"}`}>
              <span className="flex-1 truncate">{t.label}</span>
              <span className={`text-[10px] rounded-full px-2 py-0.5 ${g?.status === "publie" ? "bg-emerald-500/20 text-emerald-300" : g ? "bg-amber-500/20 text-amber-200" : "bg-zinc-800 text-zinc-400"}`}>{STATUS_LABEL[g?.status || "non_suivi"]}</span>
              {g?.next_review_at && <span className="text-[10px] text-zinc-500">révision {g.next_review_at}</span>}
            </button>); })}
        </div>
      </div>
      <div className={card} data-testid="sq-gov-form">
        {!sel ? <p className="text-sm text-zinc-500">Sélectionne un élément pour passer la checklist et changer son statut.</p> : (
          <div className="space-y-3">
            <div className="font-semibold">{sel.label}</div>
            <div className="space-y-2">{QUESTIONS.map((q, i) => (
              <label key={q} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={answers[i]} onChange={(e) => setAnswers(answers.map((a, j) => (j === i ? e.target.checked : a)))} data-testid={`sq-q-${i + 1}`} className="w-4 h-4 accent-[#C9A961]" />{q}</label>
            ))}</div>
            <div className="grid grid-cols-2 gap-2">
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-lg bg-black border border-zinc-800 p-2 text-sm" data-testid="sq-status">{Object.entries(STATUS_LABEL).filter(([k]) => k !== "non_suivi").map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
              <input value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Responsable" className="rounded-lg bg-black border border-zinc-800 p-2 text-sm" data-testid="sq-owner" />
            </div>
            <p className="text-[11px] text-zinc-500">« Publié » exige 5/5. Révision automatique dans 90 jours (30 j conseillés pour les prix).</p>
            <button onClick={submit} className="w-full rounded-xl bg-[#C9A961] text-black font-semibold py-2.5" data-testid="sq-gov-save">Enregistrer checklist + statut</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Rapprochements({ onChange }) {
  const [members, setMembers] = useState(null);
  const [unsigned, setUnsigned] = useState(null);
  const [emails, setEmails] = useState(null);
  const load = useCallback(async () => {
    const [a, b, c] = await Promise.all([api.get("/quality/members-link"), api.get("/quality/unsigned-contracts"), api.get("/quality/emails-failed")]);
    setMembers(a.data); setUnsigned(b.data); setEmails(c.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const applyLink = async () => {
    if (!window.confirm(`Relier ${members.matched} compte(s) portail à leur contrat (par courriel) ?`)) return;
    const r = await api.post("/quality/members-link?confirm=true"); toast.success(`${r.data.linked} compte(s) relié(s)`); load(); onChange();
  };
  const mkTask = async (id) => { await api.post(`/quality/emails-failed/${id}/task`); toast.success("Tâche créée"); load(); };
  if (!members || !unsigned || !emails) return <p className="text-sm text-zinc-500">Chargement…</p>;
  return (
    <div className="space-y-4">
      <div className={card} data-testid="sq-members-link">
        <div className="flex flex-wrap items-center justify-between gap-2"><b>Comptes portail ↔ contrats</b><span className="text-sm text-zinc-400">{members.total} non reliés · {members.matched} correspondance(s) par courriel</span>
          <button onClick={applyLink} disabled={!members.matched} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold disabled:opacity-40" data-testid="sq-members-apply">Relier {members.matched}</button></div>
        <ul className="mt-2 max-h-40 overflow-y-auto text-xs space-y-1">{members.rows.map((r) => <li key={r.member_id} className="flex justify-between gap-2"><span>{r.member_name || r.email}</span><span className={r.match ? "text-emerald-300" : "text-zinc-500"}>{r.match ? `→ ${r.match.full_name}` : "aucun contrat trouvé"}</span></li>)}</ul>
      </div>
      <div className={card} data-testid="sq-unsigned">
        <b>Contrats actifs non signés</b> <span className="text-sm text-zinc-400">— {unsigned.count} ({unsigned.with_email} avec courriel · {unsigned.without_email} sans · {unsigned.already_sent} déjà envoyés)</span>
        <p className="text-xs text-zinc-400 mt-1">{unsigned.next_step}</p>
        <a href="/app/eleves" className="inline-block mt-2 text-xs underline text-[#C9A961]">Ouvrir Contrats Élèves →</a>
      </div>
      <div className={card} data-testid="sq-emails-failed">
        <b>Communications échouées</b> <span className="text-sm text-zinc-400">— {emails.count}</span>
        <p className="text-xs text-zinc-500 mt-1">{emails.note}</p>
        <ul className="mt-2 space-y-1 text-xs max-h-56 overflow-y-auto">{emails.emails.map((m) => (
          <li key={m.id} className="flex items-center gap-2 rounded-lg bg-black/30 px-2 py-1.5"><span className="text-zinc-500">{(m.created_at || "").slice(0, 10)}</span><span className="flex-1 truncate">{m.subject} → {(m.to || []).join(", ")}</span>
            {m.task_id ? <span className="text-emerald-300">tâche ✓</span> : <button onClick={() => mkTask(m.id)} className="text-[#C9A961] underline" data-testid={`sq-email-task-${m.id}`}>Créer une tâche</button>}</li>
        ))}</ul>
      </div>
    </div>
  );
}

function Historique({ rows }) {
  return (
    <div className={card} data-testid="sq-history">
      <div className="text-xs uppercase tracking-widest text-zinc-400 mb-2">25 derniers changements (journal d'audit)</div>
      <ul className="space-y-1 text-xs">{rows.map((r, i) => <li key={i} className="flex gap-2"><span className="text-zinc-500 w-36 shrink-0">{(r.created_at || r.at || "").slice(0, 16).replace("T", " ")}</span><span className="text-[#C9A961] w-52 shrink-0 truncate">{r.action}</span><span className="truncate">{r.details || r.entity_type}</span></li>)}</ul>
    </div>
  );
}
